import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  AlertTriangle, ChevronRight, ChevronLeft, X, Check, 
  Package, Users, Zap, Clock, Shield, MessageSquare, Loader2
} from 'lucide-react';

const DEACTIVATION_REASONS = [
  { id: 'too_expensive', label: 'Too expensive for my budget' },
  { id: 'not_enough_features', label: 'Missing features I need' },
  { id: 'switching_competitor', label: 'Switching to a competitor' },
  { id: 'business_closing', label: 'Closing my business' },
  { id: 'technical_issues', label: 'Experiencing technical issues' },
  { id: 'poor_support', label: 'Poor customer support' },
  { id: 'not_using', label: 'Not using the platform enough' },
  { id: 'temporary_pause', label: 'Taking a temporary break' },
  { id: 'other', label: 'Other reasons' }
];

const DeactivationModal = ({ 
  isOpen, 
  onClose, 
  currentPlan, 
  subscription,
  onDeactivate,
  loading = false 
}) => {
  const [step, setStep] = useState(1);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [otherReason, setOtherReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(r => r !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleClose = () => {
    setStep(1);
    setSelectedReasons([]);
    setOtherReason('');
    onClose();
  };

  const handleConfirmDeactivation = async () => {
    setIsSubmitting(true);
    try {
      await onDeactivate({
        reasons: selectedReasons,
        other_reason: selectedReasons.includes('other') ? otherReason : '',
        deactivation_date: new Date().toISOString(),
        deletion_scheduled_date: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString() // +60 days
      });
      handleClose();
    } catch (error) {
      console.error('Deactivation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get features user will lose
  const getFeaturesToLose = () => {
    if (!currentPlan) return [];
    
    const features = [];
    
    if (currentPlan.max_listings) {
      features.push({
        icon: <Package className="w-4 h-4" />,
        label: `${currentPlan.max_listings === 0 ? 'Unlimited' : currentPlan.max_listings} Active Listings`
      });
    }
    if (currentPlan.team_members_included > 1) {
      features.push({
        icon: <Users className="w-4 h-4" />,
        label: `${currentPlan.team_members_included} Team Members`
      });
    }
    if (currentPlan.media_storage_mb) {
      features.push({
        icon: <Zap className="w-4 h-4" />,
        label: `${currentPlan.media_storage_mb >= 1024 ? `${(currentPlan.media_storage_mb / 1024).toFixed(0)} GB` : `${currentPlan.media_storage_mb} MB`} Storage`
      });
    }
    if (currentPlan.support_level) {
      const supportLabels = {
        'email': 'Email Support',
        'chat': 'Chat Support',
        'call': 'Phone Support',
        'priority': 'Priority Support',
        'dedicated': 'Dedicated Account Manager'
      };
      const support = Array.isArray(currentPlan.support_level) 
        ? currentPlan.support_level.map(s => supportLabels[s] || s).join(', ')
        : supportLabels[currentPlan.support_level] || currentPlan.support_level;
      features.push({
        icon: <MessageSquare className="w-4 h-4" />,
        label: support
      });
    }
    if (currentPlan.events_ticketing_enabled) {
      features.push({
        icon: <Zap className="w-4 h-4" />,
        label: 'Events Ticketing & QR System'
      });
    }
    if (currentPlan.gst_billing_enabled) {
      features.push({
        icon: <Shield className="w-4 h-4" />,
        label: 'GST Billing Integration'
      });
    }
    
    return features;
  };

  const canProceedFromSurvey = selectedReasons.length > 0 && 
    (!selectedReasons.includes('other') || otherReason.trim().length > 0);

  const renderStep1 = () => (
    <>
      <DialogHeader>
        <DialogTitle className="text-xl flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          Are you sure you want to cancel?
        </DialogTitle>
        <DialogDescription className="text-base pt-2">
          We&apos;re sorry to see you considering leaving. Your account and all data will be scheduled for deletion.
        </DialogDescription>
      </DialogHeader>
      
      <div className="py-6">
        <div className="bg-slate-50 rounded-lg p-4 mb-4">
          <p className="text-slate-600">
            Your <strong className="text-slate-900">{currentPlan?.plan_name}</strong> subscription 
            has been helping you grow your business. Taking a moment to reconsider?
          </p>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-900 mb-2">Need help?</h4>
          <p className="text-sm text-green-700">
            If you&apos;re facing any issues, our support team would love to help you resolve them 
            before you cancel. <a href="/help" className="underline font-medium">Contact Support</a>
          </p>
        </div>
      </div>

      <DialogFooter className="flex gap-3 sm:justify-between">
        <Button 
          variant="outline" 
          onClick={() => setStep(2)}
          className="text-slate-600"
        >
          Proceed <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
        <Button 
          onClick={handleClose}
          className="bg-primary hover:bg-primary/90"
        >
          Keep my Plan
        </Button>
      </DialogFooter>
    </>
  );

  const renderStep2 = () => (
    <>
      <DialogHeader>
        <DialogTitle className="text-xl flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-6 h-6" />
          You will lose access to:
        </DialogTitle>
        <DialogDescription className="text-base pt-2">
          If you cancel, these features will be immediately unavailable:
        </DialogDescription>
      </DialogHeader>
      
      <div className="py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
          {getFeaturesToLose().map((feature, idx) => (
            <div key={feature.id || feature.name || `${feature.title || "feature"}-${idx}`} className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                {feature.icon}
              </div>
              <span className="text-red-800 font-medium">{feature.label}</span>
            </div>
          ))}
        </div>
        
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900">All listings will be unpublished</h4>
              <p className="text-sm text-amber-700">
                Your products will no longer be visible to customers immediately upon deactivation.
              </p>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter className="flex gap-3 sm:justify-between">
        <Button 
          variant="ghost" 
          onClick={() => setStep(1)}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setStep(3)}
            className="text-slate-600"
          >
            I understand, proceed <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <Button 
            onClick={handleClose}
            className="bg-primary hover:bg-primary/90"
          >
            Keep my Plan
          </Button>
        </div>
      </DialogFooter>
    </>
  );

  const renderStep3 = () => (
    <>
      <DialogHeader>
        <DialogTitle className="text-xl">Please tell us why you&apos;re leaving</DialogTitle>
        <DialogDescription className="text-base pt-2">
          Your feedback helps us improve. Select all that apply:
        </DialogDescription>
      </DialogHeader>
      
      <div className="py-4 max-h-[400px] overflow-y-auto">
        <div className="space-y-3">
          {DEACTIVATION_REASONS.map((reason) => (
            <label 
              key={reason.id}
              htmlFor={`reason-${reason.id}`}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedReasons.includes(reason.id) 
                  ? 'bg-slate-100 border-slate-400' 
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <input
                type="checkbox"
                id={`reason-${reason.id}`}
                name={`reason-${reason.id}`}
                checked={selectedReasons.includes(reason.id)}
                onChange={() => handleReasonToggle(reason.id)}
                className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
              />
              <span className="text-slate-700">{reason.label}</span>
            </label>
          ))}
        </div>
        
        {selectedReasons.includes('other') && (
          <div className="mt-4">
            <label htmlFor="other-reason" className="text-sm font-medium text-slate-700 mb-2 block">
              Please share more details:
            </label>
            <Textarea
              id="other-reason"
              name="other-reason"
              placeholder="Tell us more about why you're leaving..."
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
        )}
      </div>

      <DialogFooter className="flex gap-3 sm:justify-between">
        <Button 
          variant="ghost" 
          onClick={() => setStep(2)}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button 
          onClick={() => setStep(4)}
          disabled={!canProceedFromSurvey}
          className="bg-slate-800 hover:bg-slate-700"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </DialogFooter>
    </>
  );

  const renderStep4 = () => (
    <>
      <DialogHeader>
        <DialogTitle className="text-xl flex items-center gap-2">
          <Shield className="w-6 h-6 text-blue-500" />
          Data Retention Policy
        </DialogTitle>
      </DialogHeader>
      
      <div className="py-6 space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 text-blue-600" />
              <span>Your account will be <strong>deactivated immediately</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 text-blue-600" />
              <span>All listings will be <strong>unpublished</strong> (moved to draft)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 text-blue-600" />
              <span>Your data will be <strong>retained for 60 days</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 text-blue-600" />
              <span>You can <strong>reactivate anytime</strong> within this period</span>
            </li>
          </ul>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-amber-600" />
            <h4 className="font-semibold text-amber-900">60-Day Countdown</h4>
          </div>
          <p className="text-sm text-amber-700">
            After 60 days, all your data including listings, orders, and customer information 
            will be <strong>permanently deleted</strong> and cannot be recovered.
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-900 mb-1">Want to come back?</h4>
          <p className="text-sm text-green-700">
            Simply log in and reactivate your account within 60 days to restore all your 
            inventory, settings, and data instantly.
          </p>
        </div>
      </div>

      <DialogFooter className="flex gap-3 sm:justify-between border-t pt-4">
        <Button 
          variant="ghost" 
          onClick={() => setStep(3)}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button 
            variant="destructive"
            onClick={handleConfirmDeactivation}
            disabled={isSubmitting || loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {isSubmitting || loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Confirm Deactivation'
            )}
          </Button>
        </div>
      </DialogFooter>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        {/* Progress Indicator */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-slate-500 mb-2">
            <span>Step {step} of 4</span>
            <span>{step === 1 ? 'Confirm' : step === 2 ? 'Impact' : step === 3 ? 'Feedback' : 'Final'}</span>
          </div>
          <Progress value={(step / 4) * 100} className="h-1" />
        </div>

        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </DialogContent>
    </Dialog>
  );
};

export default DeactivationModal;
